import { baseApi, getErrorMessage } from '@/shared/api';
import { mapIntegration } from '../libs/mapIntegration';
import type {
  ADJUST_REPORTING_INTEGRATION_ID,
  APPLE_SEARCH_ADS_REPORTING_INTEGRATION_ID,
  IRON_SOURCE_REPORTING_INTEGRATION_ID,
  LIFTOFF_REPORTING_INTEGRATION_ID,
  MINTEGRAL_REPORTING_INTEGRATION_ID,
  MOLOCO_ADS_REPORTING_INTEGRATION_ID,
  PADDLE_REPORTING_INTEGRATION_ID,
  PAY_PRO_REPORTING_INTEGRATION_ID,
  VOLUUM_REPORTING_INTEGRATION_ID,
  MERCHANTO_REPORTING_INTEGRATION_ID,
  CUSTOM_REPORTING_INTEGRATION_ID,
  APP_STORE_CONNECT_REPORTING_INTEGRATION_ID,
  APPLOVIN_ADS_REPORTING_INTEGRATION_ID,
  APPLOVIN_MAX_REPORTING_INTEGRATION_ID,
  GOOGLE_ADS_REPORTING_INTEGRATION_ID,
  META_ADS_REPORTING_INTEGRATION_ID,
  STRIPE_REPORTING_INTEGRATION_ID,
  GOOGLE_PLAY_CONSOLE_REPORTING_INTEGRATION_ID,
  APPSFLYER_REPORTING_INTEGRATION_ID,
  PAY_PAL_REPORTING_INTEGRATION_ID,
  UNITY_ADS_REPORTING_INTEGRATION_ID,
  SOLIDGATE_REPORTING_INTEGRATION_ID,
  TAPJOY_REPORTING_INTEGRATION_ID,
  X_ADS_REPORTING_INTEGRATION_ID,
  PROPELLER_ADS_REPORTING_INTEGRATION_ID,
  EVERFLOW_REPORTING_INTEGRATION_ID,
  SINGULAR_REPORTING_INTEGRATION_ID,
  IMPACT_REPORTING_INTEGRATION_ID,
  BIGO_ADS_REPORTING_INTEGRATION_ID,
  CHARGEBEE_REPORTING_INTEGRATION_ID,
  HILLTOP_ADS_REPORTING_INTEGRATION_ID,
  TRAFFIC_STARS_REPORTING_INTEGRATION_ID,
  TRAFFIC_JUNKY_REPORTING_INTEGRATION_ID,
  SCALEO_REPORTING_INTEGRATION_ID,
  BRANCH_REPORTING_INTEGRATION_ID,
  XSOLLA_REPORTING_INTEGRATION_ID,
  GOOGLE_ANALYTICS_REPORTING_INTEGRATION_ID,
  MICROSOFT_ADS_REPORTING_INTEGRATION_ID,
  PINTEREST_REPORTING_INTEGRATION_ID,
  REDDIT_REPORTING_INTEGRATION_ID,
  SNAPCHAT_ADS_REPORTING_INTEGRATION_ID,
  TIK_TOK_FOR_BUSINESS_REPORTING_INTEGRATION_ID,
  AFFISE_REPORTING_INTEGRATION_ID,
  DISPUTEUR_REPORTING_INTEGRATION_ID,
  REDTRACK_REPORTING_INTEGRATION_ID,
  FUNNELFOX_REPORTING_INTEGRATION_ID,
  PRIMER_REPORTING_INTEGRATION_ID,
  CHARGEBLAST_REPORTING_INTEGRATION_ID,
} from '../model/constants';
import type {
  ActiveStatusRecord,
  AdjustReportingIntegrationConfig,
  AppLovinAdsReportingIntegrationConfig,
  AppStoreConnectReportingIntegrationConfig,
  AppLovinMaxReportingIntegrationConfig,
  AppleSearchAdsReportingIntegrationConfig,
  StripeReportingIntegrationConfig,
  GooglePlayConsoleReportingIntegrationConfig,
  AppsflyerReportingIntegrationConfig,
  IronSourceReportingIntegrationConfig,
  LiftoffReportingIntegrationConfig,
  MerchantoReportingIntegrationConfig,
  MintegralReportingIntegrationConfig,
  MolocoAdsReportingIntegrationConfig,
  PaddleReportingIntegrationConfig,
  PayPalReportingIntegrationConfig,
  PayProReportingIntegrationConfig,
  TapJoyReportingIntegrationConfig,
  UnityAdsReportingIntegrationConfig,
  SolidgateReportingIntegrationConfig,
  VoluumReportingIntegrationConfig,
  CustomReportingIntegrationConfig,
  XAdsReportingIntegrationConfig,
  Integration,
  PropellerAdsReportingIntegrationConfig,
  EverflowReportingIntegrationConfig,
  SingularReportingIntegrationConfig,
  ImpactReportingIntegrationConfig,
  BigoAdsReportingIntegrationConfig,
  ChargebeeReportingIntegrationConfig,
  HilltopAdsReportingIntegrationConfig,
  TrafficStarsReportingIntegrationConfig,
  TrafficJunkyReportingIntegrationConfig,
  ScaleoReportingIntegrationConfig,
  BranchReportingIntegrationConfig,
  XsollaReportingIntegrationConfig,
  WebhookConfiguration,
  GoogleAnalyticsReportingIntegrationConfig,
  OAuth2Configuration,
  AffiseReportingIntegrationConfig,
  DisputeurReportingIntegrationConfig,
  RedTrackReportingIntegrationConfig,
  FunnelFoxReportingIntegrationConfig,
  PrimerReportingIntegrationConfig,
  ChargeblastReportingIntegrationConfig,
} from '../model/types';
import type { IntegrationDTO } from './types';

type AddOrganizationIntegrationParams =
  | {
      sourceIntegrationId: typeof ADJUST_REPORTING_INTEGRATION_ID;
      configuration: AdjustReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof PADDLE_REPORTING_INTEGRATION_ID;
      configuration: PaddleReportingIntegrationConfig & WebhookConfiguration;
    }
  | {
      sourceIntegrationId: typeof APPLE_SEARCH_ADS_REPORTING_INTEGRATION_ID;
      configuration: AppleSearchAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof VOLUUM_REPORTING_INTEGRATION_ID;
      configuration: VoluumReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof PAY_PRO_REPORTING_INTEGRATION_ID;
      configuration: PayProReportingIntegrationConfig & WebhookConfiguration;
    }
  | {
      sourceIntegrationId: typeof MERCHANTO_REPORTING_INTEGRATION_ID;
      configuration: MerchantoReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof APP_STORE_CONNECT_REPORTING_INTEGRATION_ID;
      configuration: AppStoreConnectReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof APPLOVIN_ADS_REPORTING_INTEGRATION_ID;
      configuration: AppLovinAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof APPLOVIN_MAX_REPORTING_INTEGRATION_ID;
      configuration: AppLovinMaxReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof STRIPE_REPORTING_INTEGRATION_ID;
      configuration: StripeReportingIntegrationConfig & WebhookConfiguration;
    }
  | {
      sourceIntegrationId: typeof GOOGLE_PLAY_CONSOLE_REPORTING_INTEGRATION_ID;
      configuration: GooglePlayConsoleReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof APPSFLYER_REPORTING_INTEGRATION_ID;
      configuration: AppsflyerReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof PAY_PAL_REPORTING_INTEGRATION_ID;
      configuration: PayPalReportingIntegrationConfig & WebhookConfiguration;
    }
  | {
      sourceIntegrationId: typeof MINTEGRAL_REPORTING_INTEGRATION_ID;
      configuration: MintegralReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof MOLOCO_ADS_REPORTING_INTEGRATION_ID;
      configuration: MolocoAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof IRON_SOURCE_REPORTING_INTEGRATION_ID;
      configuration: IronSourceReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof UNITY_ADS_REPORTING_INTEGRATION_ID;
      configuration: UnityAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof LIFTOFF_REPORTING_INTEGRATION_ID;
      configuration: LiftoffReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof SOLIDGATE_REPORTING_INTEGRATION_ID;
      configuration: SolidgateReportingIntegrationConfig & WebhookConfiguration;
    }
  | {
      sourceIntegrationId: typeof TAPJOY_REPORTING_INTEGRATION_ID;
      configuration: TapJoyReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof X_ADS_REPORTING_INTEGRATION_ID;
      configuration: XAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof CUSTOM_REPORTING_INTEGRATION_ID;
      configuration: Omit<
        CustomReportingIntegrationConfig,
        'configurationKeys'
      >;
    }
  | {
      sourceIntegrationId: typeof PROPELLER_ADS_REPORTING_INTEGRATION_ID;
      configuration: PropellerAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof EVERFLOW_REPORTING_INTEGRATION_ID;
      configuration: EverflowReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof SINGULAR_REPORTING_INTEGRATION_ID;
      configuration: Omit<
        SingularReportingIntegrationConfig,
        'withAWSCredentials'
      >;
    }
  | {
      sourceIntegrationId: typeof IMPACT_REPORTING_INTEGRATION_ID;
      configuration: ImpactReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof BIGO_ADS_REPORTING_INTEGRATION_ID;
      configuration: BigoAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof CHARGEBEE_REPORTING_INTEGRATION_ID;
      configuration: ChargebeeReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof HILLTOP_ADS_REPORTING_INTEGRATION_ID;
      configuration: HilltopAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof TRAFFIC_STARS_REPORTING_INTEGRATION_ID;
      configuration: TrafficStarsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof TRAFFIC_JUNKY_REPORTING_INTEGRATION_ID;
      configuration: TrafficJunkyReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof SCALEO_REPORTING_INTEGRATION_ID;
      configuration: ScaleoReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof BRANCH_REPORTING_INTEGRATION_ID;
      configuration: BranchReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof XSOLLA_REPORTING_INTEGRATION_ID;
      configuration: XsollaReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof GOOGLE_ANALYTICS_REPORTING_INTEGRATION_ID;
      configuration: GoogleAnalyticsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof GOOGLE_ADS_REPORTING_INTEGRATION_ID;
      configuration: OAuth2Configuration;
    }
  | {
      sourceIntegrationId: typeof META_ADS_REPORTING_INTEGRATION_ID;
      configuration: OAuth2Configuration;
    }
  | {
      sourceIntegrationId: typeof MICROSOFT_ADS_REPORTING_INTEGRATION_ID;
      configuration: OAuth2Configuration;
    }
  | {
      sourceIntegrationId: typeof PINTEREST_REPORTING_INTEGRATION_ID;
      configuration: OAuth2Configuration;
    }
  | {
      sourceIntegrationId: typeof REDDIT_REPORTING_INTEGRATION_ID;
      configuration: OAuth2Configuration;
    }
  | {
      sourceIntegrationId: typeof SNAPCHAT_ADS_REPORTING_INTEGRATION_ID;
      configuration: OAuth2Configuration;
    }
  | {
      sourceIntegrationId: typeof TIK_TOK_FOR_BUSINESS_REPORTING_INTEGRATION_ID;
      configuration: {
        authCode: string;
        code: string;
        appId: string;
      };
    }
  | {
      sourceIntegrationId: typeof AFFISE_REPORTING_INTEGRATION_ID;
      configuration: AffiseReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof DISPUTEUR_REPORTING_INTEGRATION_ID;
      configuration: DisputeurReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof REDTRACK_REPORTING_INTEGRATION_ID;
      configuration: RedTrackReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof FUNNELFOX_REPORTING_INTEGRATION_ID;
      configuration: FunnelFoxReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof PRIMER_REPORTING_INTEGRATION_ID;
      configuration: PrimerReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof CHARGEBLAST_REPORTING_INTEGRATION_ID;
      configuration: ChargeblastReportingIntegrationConfig;
    };

export const addOrganizationIntegration = async (
  params: AddOrganizationIntegrationParams,
) => {
  const { data, error } = await baseApi.functions.invoke<{
    integration: IntegrationDTO;
  }>('integrations', {
    method: 'POST',
    body: params,
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return mapIntegration(data.integration);
};

type UpdateOrganizationIntegrationParams = {
  integrationId: string;
} & (
  | {
      sourceIntegrationId: typeof ADJUST_REPORTING_INTEGRATION_ID;
      configuration: AdjustReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof APP_STORE_CONNECT_REPORTING_INTEGRATION_ID;
      configuration: AppStoreConnectReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof APPLOVIN_ADS_REPORTING_INTEGRATION_ID;
      configuration: AppLovinAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof APPLOVIN_MAX_REPORTING_INTEGRATION_ID;
      configuration: AppLovinMaxReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof APPLE_SEARCH_ADS_REPORTING_INTEGRATION_ID;
      configuration: AppleSearchAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof GOOGLE_ADS_REPORTING_INTEGRATION_ID;
      configuration: {
        useAllAccounts: boolean;
        accountListActiveStatusRecord: ActiveStatusRecord;
      };
    }
  | {
      sourceIntegrationId: typeof META_ADS_REPORTING_INTEGRATION_ID;
      configuration: {
        useAllAccounts: boolean;
        adAccountsActiveStatusRecord: ActiveStatusRecord;
      };
    }
  | {
      sourceIntegrationId: typeof STRIPE_REPORTING_INTEGRATION_ID;
      configuration: StripeReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof GOOGLE_PLAY_CONSOLE_REPORTING_INTEGRATION_ID;
      configuration: GooglePlayConsoleReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof APPSFLYER_REPORTING_INTEGRATION_ID;
      configuration: AppsflyerReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof PAY_PAL_REPORTING_INTEGRATION_ID;
      configuration: PayPalReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof MINTEGRAL_REPORTING_INTEGRATION_ID;
      configuration: MintegralReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof MOLOCO_ADS_REPORTING_INTEGRATION_ID;
      configuration: MolocoAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof IRON_SOURCE_REPORTING_INTEGRATION_ID;
      configuration: IronSourceReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof UNITY_ADS_REPORTING_INTEGRATION_ID;
      configuration: UnityAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof LIFTOFF_REPORTING_INTEGRATION_ID;
      configuration: LiftoffReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof SOLIDGATE_REPORTING_INTEGRATION_ID;
      configuration: SolidgateReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof PADDLE_REPORTING_INTEGRATION_ID;
      configuration: PaddleReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof VOLUUM_REPORTING_INTEGRATION_ID;
      configuration: VoluumReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof PAY_PRO_REPORTING_INTEGRATION_ID;
      configuration: PayProReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof MERCHANTO_REPORTING_INTEGRATION_ID;
      configuration: MerchantoReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof TAPJOY_REPORTING_INTEGRATION_ID;
      configuration: TapJoyReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof X_ADS_REPORTING_INTEGRATION_ID;
      configuration: XAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof CUSTOM_REPORTING_INTEGRATION_ID;
      configuration: Omit<
        CustomReportingIntegrationConfig,
        'configurationKeys'
      >;
    }
  | {
      sourceIntegrationId: typeof PROPELLER_ADS_REPORTING_INTEGRATION_ID;
      configuration: PropellerAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof EVERFLOW_REPORTING_INTEGRATION_ID;
      configuration: EverflowReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof SINGULAR_REPORTING_INTEGRATION_ID;
      configuration: Omit<
        SingularReportingIntegrationConfig,
        'withAWSCredentials'
      >;
    }
  | {
      sourceIntegrationId: typeof IMPACT_REPORTING_INTEGRATION_ID;
      configuration: ImpactReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof BIGO_ADS_REPORTING_INTEGRATION_ID;
      configuration: BigoAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof CHARGEBEE_REPORTING_INTEGRATION_ID;
      configuration: ChargebeeReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof HILLTOP_ADS_REPORTING_INTEGRATION_ID;
      configuration: HilltopAdsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof TRAFFIC_STARS_REPORTING_INTEGRATION_ID;
      configuration: TrafficStarsReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof TRAFFIC_JUNKY_REPORTING_INTEGRATION_ID;
      configuration: TrafficJunkyReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof SCALEO_REPORTING_INTEGRATION_ID;
      configuration: ScaleoReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof BRANCH_REPORTING_INTEGRATION_ID;
      configuration: BranchReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof XSOLLA_REPORTING_INTEGRATION_ID;
      configuration: XsollaReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof AFFISE_REPORTING_INTEGRATION_ID;
      configuration: AffiseReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof DISPUTEUR_REPORTING_INTEGRATION_ID;
      configuration: DisputeurReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof REDTRACK_REPORTING_INTEGRATION_ID;
      configuration: RedTrackReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof FUNNELFOX_REPORTING_INTEGRATION_ID;
      configuration: FunnelFoxReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof PRIMER_REPORTING_INTEGRATION_ID;
      configuration: PrimerReportingIntegrationConfig;
    }
  | {
      sourceIntegrationId: typeof CHARGEBLAST_REPORTING_INTEGRATION_ID;
      configuration: ChargeblastReportingIntegrationConfig;
    }
);

export const updateOrganizationIntegration = async (
  params: UpdateOrganizationIntegrationParams,
) => {
  const { data, error } = await baseApi.functions.invoke<{
    integration: IntegrationDTO;
  }>(`integrations/${params.integrationId}`, {
    method: 'PUT',
    body: {
      sourceIntegrationId: params.sourceIntegrationId,
      configuration: params.configuration,
    },
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return mapIntegration(data.integration);
};

export const getIntegrations = async (): Promise<Integration[]> => {
  const { data, error } = await baseApi.functions.invoke<{
    integrations: IntegrationDTO[];
  }>('integrations', {
    method: 'GET',
  });

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }

  return data.integrations.map(mapIntegration);
};

export const deleteIntegration = async (id: string) => {
  const { error } = await baseApi.functions.invoke(`integrations/${id}`, {
    method: 'DELETE',
  });

  if (error) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }
};
